import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MarkdownTypewriter } from '../../components/common/MarkdownRenderer';
import { Avatar, Button, ErrorBoundary, Spinner } from '../../components/common';
import {
  EditAnswerOptions,
  EditFooter,
  FileItem,
  Footer,
} from '../../components/onboarding';
import { useAuth } from '../../contexts/AuthContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { messageStyles } from '../../styles/style';
import { routes } from '../../lib/routes';
import { PageEdit } from 'iconoir-react';
import { useBranding } from '../../contexts/BrandingContext';
import useIsDevice from '../../hooks/useIsDevice';
import type { Content } from '../../types/uploader';

const OnboardingPage = () => {
  const { isAuthenticated, user } = useAuth();
  const { favicon } = useBranding(); // V1.5.M10 B-P1: AI avatar follows the brand
  const { isMobile } = useIsDevice();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Redirect to conversational onboarding unless explicitly using legacy flow
  // Use ?legacy=true to access the old question-by-question flow
  useEffect(() => {
    if (!searchParams.get('legacy')) {
      navigate(routes.onboardingChat, { replace: true });
    }
  }, [navigate, searchParams]);
  const {
    questions,
    isLoading,
    isQuestionLoading,
    isAnswerUpdating,
    selected,
    progress,
    isEdit,
    getQuestions,
    getSubmittedAnswers,
    getProgress,
    resetOnboarding,
  } = useOnboarding();

  const loadingRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const scrollToLoading = useCallback(() => {
    requestAnimationFrame(() => {
      if (loadingRef.current) {
        loadingRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    });
  }, []);

  const flickToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (wrapperRef.current) {
        wrapperRef.current.scrollTop = wrapperRef.current.scrollHeight;
      }
    });
  }, []);

  const scrollToLastQuestion = useCallback(() => {
    requestAnimationFrame(() => {
      if (wrapperRef.current) {
        const allQuestions = wrapperRef.current.querySelectorAll('.onboarding-question');
        const lastQuestion = allQuestions[allQuestions.length - 1];
        if (lastQuestion) {
          lastQuestion.scrollIntoView({ behavior: 'smooth' });
        }
      }
    });
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      getProgress();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && progress) {
      const answeredQuestions = progress?.meta?.answeredQuestions ?? 0;
      if (answeredQuestions > 0) {
        getSubmittedAnswers(flickToBottom);
      } else {
        getQuestions();
      }
    }
  }, [progress, isAuthenticated]);

  // NOTE: Don't reset on unmount - onboarding state should persist
  // Reset only happens when onboarding is explicitly completed

  if (isLoading) {
    return (
      <div className='flex justify-center items-center h-full my-12'>
        <Spinner size='xl' color='primary' />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className='relative w-full h-[calc(100dvh-5.55rem)] flex flex-col pb-4'>
        <div
          className='grow overflow-y-auto w-full pt-4 px-4 sm:px-0'
          ref={wrapperRef}
        >
        <div className='w-full max-w-[50.5rem] mx-auto'>
          {questions.filter(Boolean).map((message, index) => {
            // Safely parse bio response with try-catch to prevent crashes
            let bioResponse: Record<string, any> = {};
            if (message?.code === 'resume_linkedin_bio' && message.user_response) {
              try {
                bioResponse = JSON.parse(message.user_response as string);
              } catch (error) {
                console.error('Failed to parse bio response:', error);
                bioResponse = {};
              }
            }

            return (
              <div
                key={index}
                className={`onboarding-question ${message.user_response ? 'border-b border-gray-200' : ''} mb-12`}
              >
                <div className={`font-medium flex gap-4`}>
                  <Avatar
                    className='mt-1'
                    imageClassName='p-2'
                    size={isMobile ? 'md' : 'lg'}
                    shape='square'
                    src={favicon}
                    name={'C'}
                  />
                  <MarkdownTypewriter
                    speed={10}
                    markdown={message.ai_text}
                    enable={index === questions.length - 1}
                    className='text-base leading-[1.5rem] font-medium mb-0 mt-1'
                  />
                </div>

                {selected === message.id && message.is_answer_accepted ? (
                  <>
                    <EditFooter
                      questionId={message.id}
                      aiText={message.ai_text}
                      hasNestedQuestion={message.has_nested_question}
                      questionIndex={index}
                      onUpdateCallback={scrollToLastQuestion}
                    />
                    {isAnswerUpdating && (
                      <div
                        ref={loadingRef}
                        className='font-bold flex items-center gap-2 mb-12'
                      >
                        <Spinner size='sm' color='primary' />
                        <span className='animate-pulse text-gray-500'>
                          Analyzing...
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className='flex gap-3 justify-end mt-12 mb-16'>
                    <div
                      className={` ${messageStyles.answerItemClassName} !mb-0 relative group`}
                    >
                      {message.user_response && (
                        <>
                          {message.code === 'resume_linkedin_bio' ? (
                            <div className='flex flex-col gap-2'>
                              {bioResponse?.bio && (
                                <div
                                  className={`${messageStyles.messageRightClassName}`}
                                >
                                  {bioResponse?.bio}
                                </div>
                              )}
                              {bioResponse?.linkedin && (
                                <div
                                  className={`${messageStyles.messageRightClassName}`}
                                >
                                  {bioResponse?.linkedin}
                                </div>
                              )}
                              {bioResponse?.resume && (
                                <div className={``}>
                                  <FileItem
                                    file={
                                      bioResponse?.resume as unknown as Content
                                    }
                                  />
                                </div>
                              )}
                            </div>
                          ) : (
                            <div
                              className={`${messageStyles.messageRightClassName}`}
                            >
                              {message.user_input_response || message.user_response as string}
                            </div>
                          )}
                        </>
                      )}
                      {message.is_answer_accepted && (
                        <EditAnswerOptions
                          answer={message.user_input_response || message.user_response as string}
                          questionId={message.id}
                        />
                      )}

                    </div>

                    {message.user_response && (
                      <Avatar
                        // className='mt-3'
                        size={isMobile ? 'md' : 'lg'}
                        shape='square'
                        src={user?.avatar}
                        name={user?.first_name + ' ' + user?.last_name}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {isQuestionLoading && (
            <div
              ref={loadingRef}
              className='font-bold flex items-center gap-2 mb-70'
            >
              <Spinner size='sm' color='primary' />
              <span className='animate-pulse text-gray-500'>Analyzing...</span>
            </div>
          )}

          {progress?.progress && progress?.progress > 99 ? (
            <div className='flex justify-end items-center w-full mb-12'>
              <Button
                className='flex items-center gap-2 sm:mb-0 mb-2 sm:w-fit w-full'
                color='dark'
                onClick={() => navigate(`${routes.onboardingSummary}`)}
              >
                <PageEdit className='w-6 h-6' />
                Go to summary
              </Button>
            </div>
          ) : (
            <></>
          )}
        </div>
      </div>
        <div className='w-full max-w-[50.5rem] mx-auto px-4 sm:px-0'>
          <Footer
            className='sm:ml-[-0.45rem]'
            uploaderPreviewClassName='sm:ml-[-0.45rem]'
            onSubmit={isEdit ? scrollToLastQuestion : scrollToLoading}
          />
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default OnboardingPage;
